import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root',
})
export class IdleTimeoutService {
    private userActivityEvents$: Subscription | undefined;
    private idleTimer$: Subscription | undefined;
    public idleTimeoutMs!: number;

    constructor(
        private authService: AuthService,
        private userService: UserService,
        private router: Router,
        private storageService: StorageService,
        private ngZone: NgZone
    ) {
    }

    private get lastActivity(): string {
        return this.userService?.getUser()?.EntityId ? `SalSurvey_lastActivity_${this.userService?.getUser()?.EntityId}` : '';
    }

    public checkIfExpiredOnLoad(): void {
        const lastActivity = this.storageService.getData(this.lastActivity);
        if (lastActivity) {
            const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
            if (timeSinceLastActivity > this.idleTimeoutMs) {
                this.handleTimeout();
                return;
            }
        }

        this.startWatching();
    }

    private startWatching(): void {
        this.ngZone.runOutsideAngular(() => {
            const events$ = merge(
                fromEvent(document, 'mousemove'),
                fromEvent(document, 'keydown'),
                fromEvent(document, 'mousedown'),
                fromEvent(document, 'touchstart')
            ).pipe(debounceTime(500));
            this.userActivityEvents$ = events$.subscribe(() => this.resetTimer());
        });
        this.resetTimer();
    }

    public stopWatching(): void {
        this.userActivityEvents$?.unsubscribe();
        this.idleTimer$?.unsubscribe();
        this.storageService.removeData(this.lastActivity);
    }

    private resetTimer(): void {
        this.idleTimer$?.unsubscribe();
        this.storageService.saveData(this.lastActivity, Date.now().toString());

        if (this.idleTimeoutMs) {
            this.idleTimer$ = timer(+this.idleTimeoutMs).subscribe(() => {
                this.ngZone.run(() => {
                    this.handleTimeout();
                });
            });
        }
    }

    private handleTimeout(): void {
        this.stopWatching();
        this.authService.logout(async () => {
            await this.router.navigate(['/login']);
        });
    }
}
